import { getLocale, toIntlLocale, type AppLocale } from '@/config/locale'
import { translate } from '@/i18n'

function normalizeSource(value?: string | null) {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function resolveSource(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  return normalizeSource(kickoffUtc) ?? normalizeSource(kickoffFallback)
}

export function parseKickoffDate(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  const source = resolveSource(kickoffUtc, kickoffFallback)
  if (!source) return null

  const date = new Date(source)
  return Number.isNaN(date.getTime()) ? null : date
}

export function getKickoffSource(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  return resolveSource(kickoffUtc, kickoffFallback)
}

export function getKickoffTimeValue(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  return parseKickoffDate(kickoffUtc, kickoffFallback)?.getTime() ?? Number.MAX_SAFE_INTEGER
}

export function getKickoffDateKey(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  const date = parseKickoffDate(kickoffUtc, kickoffFallback)
  if (!date) return 'unknown'

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function formatKickoff(
  kickoffUtc: string | null | undefined,
  kickoffFallback: string | null | undefined,
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
  emptyLabel: string,
) {
  const activeLocale = locale ?? getLocale()
  const source = resolveSource(kickoffUtc, kickoffFallback)
  if (!source) return emptyLabel.startsWith('-') ? emptyLabel : translate(emptyLabel, activeLocale as AppLocale)

  const date = new Date(source)
  if (Number.isNaN(date.getTime())) return source

  return new Intl.DateTimeFormat(toIntlLocale(activeLocale), options).format(date)
}

export function formatKickoffTime(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      hour: '2-digit',
      minute: '2-digit',
    },
    '--:--',
  )
}

export function formatKickoffShortDate(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      day: 'numeric',
      month: 'short',
    },
    '--',
  )
}

export function formatKickoffCompactDateTime(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    },
    'Aftrap onbekend',
  )
}

export function formatKickoffDateTime(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    },
    'Aftrap onbekend',
  )
}

export function formatKickoffLongDateTime(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    },
    'Aftrap onbekend',
  )
}

export function formatKickoffDateHeading(
  kickoffUtc?: string | null,
  kickoffFallback?: string | null,
  locale?: string,
) {
  return formatKickoff(
    kickoffUtc,
    kickoffFallback,
    locale,
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    },
    'Datum onbekend',
  )
}
